/**
 * Progress of a multi-screen lead form within one page view (#1469).
 *
 * A walk-through form (`LeadFormEmbed`) records the screens the visitor
 * finished on every "Next"; leaving mid-way sends them as a partial lead, and
 * the full form later completes that lead under the same draft id.
 *
 * Memory only, never localStorage: a stored copy would keep a name, phone and
 * consent answer on the device after delivery, readable by every script on the
 * host site and by the next person using it. The partial goes out the moment
 * the page goes to the background, which is all a stored copy ever covered.
 *
 * Kept per form across mounts, so a form that's closed and reopened on the
 * same page (the landing unmounts it on close) can still complete its partial
 * lead — but only for the same visitor: `sameContact` decides.
 *
 * Draft shape: `{ id, rev, sentRev, payload }`.
 * - `id` — this form fill; travels as `payload.progress.draftId`.
 * - `rev` — bumps on every "Next"; `sentRev` — the last rev the API accepted.
 * - `payload` — the ready-to-send lead, finished screens only, with `progress`.
 */

const drafts = new Map();

/** A fresh form-fill id (what the API's `progress.draftId` accepts). */
export function newDraftId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** @param {string} formKey */
export function getLeadFormDraft(formKey) {
  return drafts.get(formKey) || null;
}

/**
 * @param {string} formKey
 * @param {{id: string, rev: number, sentRev: number, payload: object}} draft
 */
export function setLeadFormDraft(formKey, draft) {
  drafts.set(formKey, draft);
}

/** @param {string} formKey */
export function clearLeadFormDraft(formKey) {
  drafts.delete(formKey);
}

/** Tests only: forget every draft. */
export function __resetLeadFormDrafts() {
  drafts.clear();
}

const normalizePhone = (v) => (typeof v === 'string' ? v.replace(/\D/g, '') : '');
const normalizeEmail = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/**
 * Do two lead payloads belong to the same visitor? Same phone or same email.
 * A reopened form continues an earlier partial lead only then — otherwise the
 * next person on a shared device would overwrite the first person's lead.
 *
 * @param {{phone?: string, email?: string} | null} a
 * @param {{phone?: string, email?: string} | null} b
 */
export function sameContact(a, b) {
  if (!a || !b) return false;
  const phone = normalizePhone(a.phone);
  if (phone && phone === normalizePhone(b.phone)) return true;
  const email = normalizeEmail(a.email);
  return !!email && email === normalizeEmail(b.email);
}
